// Copyright 2024 Sorama B.V.
import {
    CriticalBandSpectrumSet,
    GenericMeasurement,
    MetricPsychoAcousticsProperties,
    PsychoAcousticsAnalysisProperties,
    PsychoAcousticsBandsResults,
    PsychoAcousticsOverTimeResults,
    PsychoAcousticsResults,
} from "datatypes/SoramaWebApi";

export abstract class IPsychoAcousticsProcessingService {
    /**
     * Get psychoacoustics for the given measurement
     * @param measurement Measurement to get the psychoacoustics from
     * @param properties Properties for the psychoacoustics analysis
     * @param computationProgressCallback Optional progress callback which will receive periodic progress updates
     * @param abortSignal Optional abort signal to cancel the computation
     */
    abstract getPsychoAcoustics(
        measurement: GenericMeasurement,
        properties: PsychoAcousticsAnalysisProperties,
        computationProgressCallback?: (progress: number) => void,
        abortSignal?: AbortSignal
    ): Promise<PsychoAcousticsResults>;

    /**
     * Get a psychoacoustic metric over time for the given measurement
     * @param measurement Measurement to get the psychoacoustics from
     * @param properties Properties for the psychoacoustics analysis, should contain the metric type
     * @param computationProgressCallback Optional progress callback which will receive periodic progress updates
     */
    abstract getPsychoAcousticMetricOverTime(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<PsychoAcousticsOverTimeResults>;

    /**
     * Get a psychoacoustic metric spectrum
     * @param measurement Measurement to get the psychoacoustics from
     * @param properties Properties for the psychoacoustics analysis, should contain the metric type
     * @param computationProgressCallback Optional progress callback which will receive periodic progress updates
     */
    abstract getPsychoAcousticMetricBands(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<PsychoAcousticsBandsResults>;

    /**
     * Get a psychoacoustic metric critical band spectrogram (spectrum over time)
     * @param measurement Measurement to get the psychoacoustics from
     * @param properties Properties for the psychoacoustics analysis, should contain the metric type
     * @param computationProgressCallback Optional progress callback which will receive periodic progress updates
     */
    abstract getPsychoAcousticsCriticalBandSet(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<CriticalBandSpectrumSet>;
}
